import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Container, Box, Paper, Typography, Grid, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, CircularProgress, FormControl,
  Select, MenuItem, TextField, Skeleton, Tabs, Tab,
} from '@mui/material';
import {
  Download as DownloadIcon,
  CheckCircleOutline,
  ErrorOutline,
  HourglassEmpty,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import Footer from '../../components/Footer/Footer';
import { useAuth } from '../../context/AuthContext';
import DataTablePagination from '../../components/Table/DataTablePagination';
import SearchField from '../../components/Table/SearchField';
import DepartmentSelect from '../../components/Departments/DepartmentSelect';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import Panel from '../../components/Reports/Panel';
import StatTile from '../../components/Reports/StatTile';
import BarChart from '../../components/Reports/BarChart';
import HeatGrid from '../../components/Reports/HeatGrid';
import FilterChips from '../../components/Reports/FilterChips';
import { chipFor } from '../../components/Tasks/taskColumns';
import { allStatusBars } from '../../utils/supplierTiles';
import { CONTRACT_OPTIONS, COMPLETION_OPTIONS, APPLICABILITY_LABEL, optionLabel } from '../../utils/contractApplicability';
import { PIPELINE_RAMP, AGE_RAMP, SERIES, STATUS, INK, FONT } from '../../components/Reports/vizTokens';

const STATUSES = [
  'draft', 'submitted', 'under_review', 'pending_legal', 'pending_procurement',
  'pending_contract_upload', 'approved', 'completed', 'rejected', 'more_info_required',
];

const humanise = (v) => String(v || '')
  .split('_').filter(Boolean)
  .map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const EMPTY_SUMMARY = {
  total: 0, byStatus: [], byDepartment: [], pipeline: [], outcomes: [],
  exception: null, unrecognised: null, byDepartmentStage: [], byAge: [], oldestWaitingDays: 0,
};

const Reports = () => {
  // Seeded from the query string so a link from the Overview arrives already
  // filtered. Read once at mount: after that the controls own the state, and
  // re-syncing on every change would fight the user's own edits.
  const [searchParams] = useSearchParams();
  const initial = (key) => searchParams.get(key) || '';

  // Tab lives in the URL so a drill-down link reopens on the list it filtered,
  // and so the browser back button steps between the two views.
  const [tab, setTabState] = useState(initial('tab') === 'suppliers' ? 'suppliers' : 'summary');
  const setTab = useCallback((next) => {
    setTabState(next);
    const params = new URLSearchParams(window.location.search);
    if (next === 'summary') params.delete('tab'); else params.set('tab', next);
    const qs = params.toString();
    window.history.replaceState({}, '', qs ? `?${qs}` : window.location.pathname);
  }, []);

  const [search, setSearch] = useState(initial('search'));
  const debouncedSearch = useDebouncedValue(search);
  const { user } = useAuth();
  // Management is confined to its own department server-side. Pinning the
  // control here is presentation only — it stops the page offering a choice the
  // server will refuse, rather than being the thing that enforces it.
  const pinnedDepartment = user?.role === 'management' ? (user.department || '') : null;
  const [department, setDepartment] = useState(pinnedDepartment ?? initial('department'));
  const [status, setStatus] = useState(initial('status'));
  const [stage, setStage] = useState(initial('stage'));
  const [from, setFrom] = useState(initial('from'));
  const [to, setTo] = useState(initial('to'));
  const [contract, setContract] = useState(initial('contract'));
  const [completion, setCompletion] = useState(initial('completion'));

  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);

  // One place builds the filter for both the fetch and the export link, for
  // the same reason the server has one query builder.
  const filterParams = useCallback(() => {
    const p = {};
    if (debouncedSearch) p.search = debouncedSearch;
    if (department) p.department = department;
    if (status) p.status = status;
    if (stage) p.stage = stage;
    if (from) p.from = from;
    if (to) p.to = to;
    if (contract) p.contract = contract;
    if (completion) p.completion = completion;
    return p;
  }, [debouncedSearch, department, status, stage, from, to, contract, completion]);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/reports/suppliers', {
        params: { ...filterParams(), page, limit: rowsPerPage },
      });
      setSummary({ ...EMPTY_SUMMARY, ...(res.data.summary || {}) });
      setRows(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
    } catch (error) {
      toast.error('Failed to build the report');
    } finally {
      setLoading(false);
    }
  }, [filterParams, page, rowsPerPage]);

  useEffect(() => { fetchReport(); }, [fetchReport]);
  useEffect(() => { setPage(1); }, [debouncedSearch, department, status, stage, from, to, contract, completion, rowsPerPage]);

  // `byDepartment` downloads the workbook with one sheet per department.
  const handleExport = async (format, { byDepartment = false } = {}) => {
    try {
      setExporting(byDepartment ? 'departments' : format);
      // Fetched as a blob through the api client so the Authorization header
      // is attached — a plain link would hit the endpoint unauthenticated.
      const res = await api.get(byDepartment ? '/reports/suppliers/export/departments' : '/reports/suppliers/export', {
        params: { ...filterParams(), format },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', byDepartment
        ? `supplier-report-by-department-${stamp}.xlsx`
        : `supplier-report-${stamp}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(`Failed to export the ${format.toUpperCase()}`);
    } finally {
      setExporting('');
    }
  };

  const clearAll = () => {
    setSearch(''); setStatus(''); setStage(''); setFrom(''); setTo('');
    setContract(''); setCompletion('');
    // A pinned department survives Clear — it is not the reader's to clear, and
    // dropping it here would only send a request the server re-narrows anyway.
    setDepartment(pinnedDepartment ?? '');
  };

  // Clicking a stage filters by stage, not status: the "Procurement review" bar
  // counts four statuses, and a status filter would return fewer rows than the
  // number the reader just clicked.
  // Every drill-down narrows the filters and hands the reader the list those
  // filters describe. Staying on the charts would leave them to notice the
  // numbers moved and then go looking for the rows themselves.
  const selectStage = (row) => {
    setStatus('');
    setStage(row.key);
    setTab('suppliers');
  };

  const selectDepartment = (row) => {
    setDepartment(row.key);
    setTab('suppliers');
  };

  const selectCell = (dept, cellStage) => {
    setDepartment(dept === 'Unassigned' ? '__unassigned' : dept);
    setStatus('');
    setStage(cellStage.key);
    setTab('suppliers');
  };

  const inFlight = useMemo(
    () => (summary.pipeline || [])
      .filter((s) => s.key !== 'draft')
      .reduce((n, s) => n + s.count, 0) + (summary.exception?.count || 0),
    [summary]
  );

  const overdue = useMemo(
    () => (summary.byAge || []).find((b) => b.key === '30_plus')?.count || 0,
    [summary]
  );

  // Only 'completed' is finished. 'approved' used to be counted here too, but it
  // is an old holding status still waiting on a vendor number, so it now sits
  // in the pipeline instead.
  const approved = useMemo(
    () => (summary.outcomes || []).find((o) => o.key === 'completed')?.count || 0,
    [summary]
  );

  const departmentRows = useMemo(
    () => (summary.byDepartment || []).map((r) => ({
      key: r.department === 'Unassigned' ? '__unassigned' : r.department,
      label: r.department,
      count: r.count,
    })),
    [summary]
  );

  // Every status, completed and rejected included, so each department's row
  // accounts for all of its suppliers.
  const crossStages = useMemo(
    () => [...(summary.pipeline || []), summary.exception, ...(summary.outcomes || [])].filter(Boolean)
      .map((s) => ({ key: s.key, label: s.label })),
    [summary]
  );

  const crossRows = useMemo(
    () => (summary.byDepartmentStage || [])
      .filter((r) => crossStages.some((s) => r.stages.find((c) => c.key === s.key)?.count > 0)),
    [summary, crossStages]
  );

  // A pinned department is not a filter the reader chose, so it does not count
  // towards "filters are active" and gets no chip — otherwise a management user
  // sees a chip they cannot clear and a Clear button that never goes quiet.
  const chosenDepartment = pinnedDepartment ? '' : department;
  const hasFilters = Boolean(search || chosenDepartment || status || stage || from || to || contract || completion);

  // Human labels, not raw keys: a chip reading "Stage: legal" is barely better
  // than no chip at all.
  const stageLabel = (key) => [...(summary.pipeline || []), summary.exception, ...(summary.outcomes || []), summary.unrecognised]
    .filter(Boolean).find((s) => s.key === key)?.label || key;
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '');

  const activeFilters = [
    { key: 'search', label: 'Search', value: search, onClear: () => setSearch('') },
    {
      key: 'department', label: 'Department', value: chosenDepartment,
      display: chosenDepartment === '__unassigned' ? 'Unassigned' : chosenDepartment,
      onClear: () => setDepartment(''),
    },
    { key: 'status', label: 'Status', value: status, display: humanise(status), onClear: () => setStatus('') },
    { key: 'stage', label: 'Stage', value: stage, display: stageLabel(stage), onClear: () => setStage('') },
    { key: 'from', label: 'From', value: from, display: fmtDate(from), onClear: () => setFrom('') },
    { key: 'to', label: 'To', value: to, display: fmtDate(to), onClear: () => setTo('') },
    {
      key: 'contract', label: 'Contract', value: contract,
      display: optionLabel(CONTRACT_OPTIONS, contract), onClear: () => setContract(''),
    },
    {
      key: 'completion', label: 'Completion', value: completion,
      display: optionLabel(COMPLETION_OPTIONS, completion), onClear: () => setCompletion(''),
    },
  ];

  const inputSx = {
    ...FONT,
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#fff', borderRadius: '8px', fontSize: '14px',
      '& fieldset': { borderColor: INK.border },
    },
  };

  // Past the pipeline's ramp, a bar is coloured by what its status means: the
  // label beside it carries the meaning too, never the colour alone.
  const STATUS_BAR_COLOR = { more_info: STATUS.warning, completed: STATUS.good, rejected: STATUS.critical, other: INK.muted };
  const statusRows = allStatusBars(summary).map((row) => (
    STATUS_BAR_COLOR[row.key] ? { ...row, color: STATUS_BAR_COLOR[row.key] } : row
  ));

  const loadingBlock = (height) => <Skeleton variant="rounded" height={height} sx={{ borderRadius: '10px' }} />;

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#fff', display: 'flex', flexDirection: 'column' }}>
      <Container maxWidth="lg" sx={{ pt: { xs: 3, sm: 5 }, pb: { xs: 3, sm: 4 }, px: { xs: 2, sm: 3 }, flex: 1 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ ...FONT, fontWeight: 'bold', mb: 1, color: INK.primary, fontSize: { xs: '22px', sm: '28px' } }}>
            Reports
          </Typography>
          <Typography sx={{ ...FONT, color: INK.secondary, fontSize: '14px' }}>
            Where every supplier application is sitting, who owns it, and how long it has waited.
            Click any bar or cell to open the matching suppliers.
          </Typography>
        </Box>

        {/* Filters — one row above the charts. */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          <SearchField value={search} onChange={setSearch} placeholder="Company or vendor no." />
          <Box sx={{ minWidth: 190 }}>
            <DepartmentSelect
              value={pinnedDepartment ?? department}
              onChange={setDepartment}
              label="Department"
              includeAll={!pinnedDepartment}
              disabled={Boolean(pinnedDepartment)}
            />
          </Box>
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <Select
              value={status}
              onChange={(e) => { setStatus(e.target.value); setStage(''); }}
              displayEmpty
              sx={{ ...inputSx, fontSize: '14px', backgroundColor: '#fff', borderRadius: '8px' }}
            >
              <MenuItem value="">All statuses</MenuItem>
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s} sx={{ ...FONT, fontSize: '14px' }}>{humanise(s)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={contract}
              onChange={(e) => setContract(e.target.value)}
              displayEmpty
              sx={{ ...inputSx, fontSize: '14px', backgroundColor: '#fff', borderRadius: '8px' }}
            >
              <MenuItem value="">Any contract applicability</MenuItem>
              {CONTRACT_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value} sx={{ ...FONT, fontSize: '14px' }}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <Select
              value={completion}
              onChange={(e) => setCompletion(e.target.value)}
              displayEmpty
              sx={{ ...inputSx, fontSize: '14px', backgroundColor: '#fff', borderRadius: '8px' }}
            >
              <MenuItem value="">Completed or not</MenuItem>
              {COMPLETION_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value} sx={{ ...FONT, fontSize: '14px' }}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField type="date" size="small" label="From" value={from}
            onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={inputSx} />
          <TextField type="date" size="small" label="To" value={to}
            onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={inputSx} />
          {hasFilters && (
            <Button size="small" onClick={clearAll}
              sx={{ ...FONT, textTransform: 'none', fontSize: '13px', color: '#374151' }}>
              Clear
            </Button>
          )}
        </Box>

        <FilterChips filters={activeFilters} onClearAll={clearAll} />

        {/* Filters and search sit ABOVE the tabs: both views answer the same
            question, and a filter that reset when you switched tabs would make
            the drill-down below meaningless. */}
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          sx={{
            mb: 3,
            borderBottom: `1px solid ${INK.grid}`,
            minHeight: 40,
            '& .MuiTab-root': {
              ...FONT, textTransform: 'none', fontSize: '14px', fontWeight: 500,
              minHeight: 40, color: INK.secondary,
            },
            '& .Mui-selected': { color: INK.primary },
          }}
        >
          <Tab value="summary" label="Summary" />
          <Tab value="suppliers" label={`Suppliers${total ? ` (${total.toLocaleString()})` : ''}`} />
        </Tabs>

        {tab === 'summary' && (<>
        {/* Headline figures */}
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={12} sm={6} md={3}>
            {loading ? loadingBlock(122) : (
              <StatTile hero label="Suppliers matched" value={summary.total}
                note={hasFilters ? 'with the filters above' : 'all applications'} />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {loading ? loadingBlock(122) : (
              <StatTile
                label="In flight"
                value={inFlight}
                note="awaiting a decision from us"
                icon={<HourglassEmpty />}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {loading ? loadingBlock(122) : (
              <StatTile
                label="Waiting over 30 days"
                value={overdue}
                accent={overdue > 0 ? STATUS.critical : undefined}
                // 4.68:1 on white — the one status hue that clears body-text
                // contrast, so the alarm may carry into the note.
                noteTone={overdue > 0 ? STATUS.critical : undefined}
                icon={overdue > 0 ? <ErrorOutline /> : undefined}
                note={summary.oldestWaitingDays
                  ? `longest wait ${summary.oldestWaitingDays} days`
                  : 'nothing is overdue'}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {loading ? loadingBlock(122) : (
              <StatTile
                label="Completed"
                value={approved}
                accent={STATUS.good}
                icon={<CheckCircleOutline />}
                note={summary.total ? `${Math.round((approved / summary.total) * 100)}% of matched` : undefined}
              />
            )}
          </Grid>
        </Grid>

        {/* The pipeline — the centrepiece. */}
        <Box sx={{ mb: 2.5 }}>
          {loading ? loadingBlock(300) : (
            <Panel
              title="Where applications are sitting"
              subtitle="Every status, in workflow order and then how applications ended — so a queue backing up shows as a shape rather than a number you have to hunt for."
            >
              <BarChart
                rows={statusRows}
                colors={PIPELINE_RAMP}
                onSelect={selectStage}
                selected={stage}
                emptyText="No applications match these filters"
              />
            </Panel>
          )}
        </Box>

        <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
          <Grid item xs={12} md={6}>
            {loading ? loadingBlock(260) : (
              <Panel
                alignSubtitle
                title="By department"
                subtitle="Who owns the work. Unassigned is not a department — it is work nobody has picked up."
              >
                <BarChart
                  rows={departmentRows}
                  colors={SERIES}
                  onSelect={selectDepartment}
                  selected={department}
                  emptyText="No departments to show"
                />
              </Panel>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {loading ? loadingBlock(260) : (
              <Panel
                alignSubtitle
                title="How long they have waited"
                subtitle="Days since submission, for applications still awaiting a decision. Drafts sit with the supplier, so they are not counted."
              >
                <BarChart
                  rows={summary.byAge}
                  colors={AGE_RAMP}
                  emptyText="Nothing is currently waiting"
                />
              </Panel>
            )}
          </Grid>
        </Grid>

        <Box sx={{ mb: 3 }}>
          {loading ? loadingBlock(260) : (
            <Panel
              title="Department × status"
              subtitle="Every supplier, by department and status. The download has a sheet per department listing its suppliers."
              action={(
                <Button
                  variant="outlined" size="small" startIcon={<DownloadIcon />}
                  disabled={Boolean(exporting) || summary.total === 0}
                  onClick={() => handleExport('xlsx', { byDepartment: true })}
                  sx={{ ...FONT, textTransform: 'none', fontSize: '13px', borderColor: INK.border, color: '#374151', flexShrink: 0 }}
                >
                  {exporting === 'departments' ? 'Preparing…' : 'Download by department'}
                </Button>
              )}
            >
              <HeatGrid
                rows={crossRows}
                stages={crossStages}
                onSelect={selectCell}
                onSelectTotal={selectStage}
                emptyText="No suppliers to show"
              />
            </Panel>
          )}
        </Box>

        </>)}

        {tab === 'suppliers' && (<>
        {/* Export lives with the list it exports. */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography sx={{ ...FONT, fontSize: '13px', color: INK.muted, mr: 'auto' }}>
            {total.toLocaleString()} {total === 1 ? 'supplier matches' : 'suppliers match'} these filters
          </Typography>
          <Button
            variant="outlined" size="small" startIcon={<DownloadIcon />}
            disabled={Boolean(exporting) || summary.total === 0}
            onClick={() => handleExport('xlsx')}
            sx={{ ...FONT, textTransform: 'none', fontSize: '13px', borderColor: INK.border, color: '#374151' }}
          >
            {exporting === 'xlsx' ? 'Preparing…' : 'Export Excel'}
          </Button>
          <Button
            variant="outlined" size="small" startIcon={<DownloadIcon />}
            disabled={Boolean(exporting) || summary.total === 0}
            onClick={() => handleExport('pdf')}
            sx={{ ...FONT, textTransform: 'none', fontSize: '13px', borderColor: INK.border, color: '#374151' }}
          >
            {exporting === 'pdf' ? 'Preparing…' : 'Export PDF'}
          </Button>
        </Box>

        {/* Detail — the table view every chart above is also readable through. */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 240, alignItems: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${INK.border}`, borderRadius: '10px', overflow: 'hidden' }}>
            <Table sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: INK.plane }}>
                  {['Supplier', 'Vendor no.', 'Department', 'Status', 'Contract', 'Submitted'].map((h) => (
                    <TableCell key={h} sx={{ ...FONT, fontSize: '13px', fontWeight: 500, color: '#4b5563', py: 1.5 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography sx={{ ...FONT, color: INK.secondary, fontSize: '13px' }}>
                        No suppliers match these filters
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : rows.map((row) => {
                  // Shared with both task queues, so one status never wears two
                  // colours depending on which page you opened it from.
                  const chip = chipFor(row.status);
                  return (
                    <TableRow key={row._id} sx={{ '&:hover': { backgroundColor: '#fcfcfc' } }}>
                      <TableCell sx={{ ...FONT, fontSize: '14px', color: INK.primary, py: 1.5 }}>{row.supplierName}</TableCell>
                      <TableCell sx={{ ...FONT, fontSize: '14px', color: INK.primary, py: 1.5, fontVariantNumeric: 'tabular-nums' }}>{row.vendorNumber || '-'}</TableCell>
                      <TableCell sx={{ ...FONT, fontSize: '14px', color: INK.primary, py: 1.5 }}>
                        {row.department || <Typography component="span" sx={{ ...FONT, color: INK.muted, fontSize: '13px', fontStyle: 'italic' }}>Unassigned</Typography>}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Chip label={humanise(row.status)} size="small"
                          sx={{ ...FONT, backgroundColor: chip.bg, color: chip.color, fontSize: '12px', height: '24px', borderRadius: '12px', fontWeight: 600 }} />
                      </TableCell>
                      <TableCell sx={{ ...FONT, fontSize: '14px', color: row.contractApplicability ? INK.primary : INK.muted, py: 1.5 }}>
                        {APPLICABILITY_LABEL[row.contractApplicability] || '-'}
                      </TableCell>
                      <TableCell sx={{ ...FONT, fontSize: '14px', color: INK.primary, py: 1.5, fontVariantNumeric: 'tabular-nums' }}>
                        {(row.slaMetrics?.submissionDate || row.submittedAt)
                          ? new Date(row.slaMetrics?.submissionDate || row.submittedAt).toLocaleDateString('en-GB')
                          : '-'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <DataTablePagination
              page={page}
              total={total}
              rowsPerPage={rowsPerPage}
              onPageChange={setPage}
              onRowsPerPageChange={setRowsPerPage}
              rowsPerPageOptions={[25, 50, 100]}
              loading={loading}
            />
          </TableContainer>
        )}
        </>)}
      </Container>
      <Footer />
    </Box>
  );
};

export default Reports;
